import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { BaseModalType } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import { Input, PopoverInput } from '@ui/components/Input'
import { Modal } from '@ui/components/Modal'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { SubIBAccount } from '@model/MultiTierDto'

import { Box } from '@shared/components/Box'

const INPUT_FIELD_WIDTH = '232px'

interface AddEditSubIBModalProps {
  data: SubIBAccount
  handleSubIB(subAccountId: number, commissionPlanGroupId: string): void
}

export const AddEditSubIBModal: BaseModalType<AddEditSubIBModalProps> = (props) => {
  const { handleSubIB, close, data } = props

  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const optionsFetch = useIBAccountPlanOptionsFetch(props)
  const { options, isLoading } = optionsFetch
  const isDropdownDisabled = !options.length || (options.length === 1 && options[0].isDefault)

  const formValidationProps = useFormValidation({ ...props, ...optionsFetch })
  const { isValid, commissionPlan, setCommissionPlan, error } = formValidationProps

  const handleSubmit = async () => handleSubIB(data.account.id, commissionPlan)

  const rowProps = isMobile
    ? {
        direction: 'column' as const,
      }
    : {
        justify: 'space-between' as const,
        align: 'center' as const,
      }

  const inputWrapperProps = {
    flexItem: isMobile ? undefined : `0 0 ${INPUT_FIELD_WIDTH}`,
  }

  return (
    <Modal>
      <Modal.Header canClose={!isMobile}>{t('IB.MultiTier.Edit Sub-IB Account')}</Modal.Header>

      <Modal.Body isLoading={isLoading}>
        <Box flex direction='column' gap='x8' fullWidth>
          <Box fullWidth flex gap='x8' {...rowProps}>
            <Body2>{t('IB.MultiTier.Sub-IB Account')}:</Body2>
            <Box {...inputWrapperProps}>
              <Input value={data.account.name} type='text' readOnly disabled size='lg' />
            </Box>
          </Box>

          <Box fullWidth flex gap='x8' {...rowProps}>
            <Body2>{t('IB.MultiTier.IB Commission Plan')}</Body2>
            <Box {...inputWrapperProps}>
              <PopoverInput
                label={isMobile ? t('IB.MultiTier.Default Plan') : undefined}
                value={commissionPlan}
                options={options}
                onChange={(option) => setCommissionPlan(String(option.id))}
                hint={error}
                error={!!error}
                disabled={isDropdownDisabled}
                isFullWidth
                size='lg'
              />
            </Box>
          </Box>
        </Box>
      </Modal.Body>

      <Modal.Footer>
        {isMobile && <Modal.Footer.Secondary onClick={close}>{t('Close')}</Modal.Footer.Secondary>}
        <Modal.Footer.Primary disabled={!isValid} onClick={handleSubmit}>
          {t('Submit')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}

type FormValidationProps = AddEditSubIBModalProps & ReturnType<typeof useIBAccountPlanOptionsFetch>

const useFormValidation = (props: FormValidationProps) => {
  const { data, options = [] } = props

  const { t } = useTranslation()

  const [commissionPlan, setCommissionPlan] = useState<string>(data.commissionPlanGroup.id)
  const [error, setError] = useState<string>()

  useEffect(() => {
    setError(undefined)

    if (!options) {
      return
    }

    const originalPlan = options.find((plan) => plan.id === data.commissionPlanGroup.id)
    const selectedPlan = options.find((plan) => plan.id === commissionPlan)

    if (!originalPlan || !selectedPlan) {
      return
    }

    const originalAmounts: { [key: number]: number } = {}
    originalPlan.commissionPlans.forEach((cp) => {
      originalAmounts[cp.tradingAccountType.id] = cp.amount
    })

    for (const cp of selectedPlan.commissionPlans) {
      if (cp.amount < originalAmounts[cp.tradingAccountType.id]) {
        setError(t('IB.MultiTier.IB Commission plan cannot be downgraded'))
        break
      }
    }
  }, [commissionPlan, options, data, t])

  const isValid = commissionPlan === data.commissionPlanGroup.id || !error

  return { error, setError, isValid, commissionPlan, setCommissionPlan }
}

const useIBAccountPlanOptionsFetch = (props: AddEditSubIBModalProps) => {
  const { data } = props

  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()

  const introducingBrokerId = account?.clientIntroducingBroker?.id || ''

  const { data: options = [], isLoading } = useQuery({
    queryKey: queryKeys.ib.subAccountPlanOptions(introducingBrokerId),
    queryFn: async () => {
      const response = await apiClient.getIBSubAccountPlanOptions(introducingBrokerId)
      return response.filter((plan) => data.commissionPlanGroup.id === plan.id || !plan.isDefault)
    },
    enabled: !!introducingBrokerId,
  })

  return { options, isLoading }
}
